import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Box, Button, Typography, Chip, useTheme,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';
import PaidRoundedIcon from '@mui/icons-material/PaidRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import TouchAppRoundedIcon from '@mui/icons-material/TouchAppRounded';
import FlagRoundedIcon from '@mui/icons-material/FlagRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import SyncRoundedIcon from '@mui/icons-material/SyncRounded';
import { Page, Card, StatCard } from '../ui';

const PERIODS = [
  { label: 'Сегодня', value: 'today' },
  { label: 'Вчера', value: 'yesterday' },
  { label: '7 дней', value: '7d' },
  { label: '30 дней', value: '30d' },
  { label: '90 дней', value: '90d' },
];

const fmtInt = (n) => (n === null || n === undefined ? '—' : Number(n).toLocaleString('ru-RU'));
const fmtPct = (n) => (!n ? '—' : Number(n).toFixed(2) + '%');
const fmtLeads = (n) => (!n ? '—' : Number(n).toLocaleString('ru-RU'));
const fmtCurrency = (n) =>
  n === null || n === undefined ? '—' : '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const STATUS_COLOR = { ok: 'success', banned: 'error' };

// Цвета серий - из палитры референса (phosphor/sky/ultraviolet/amber/pearl),
// чтобы график оставался в системе, а не превращался в радугу
const METRICS = {
  spend: { label: 'Расход', color: '#5fed83', fmt: (v) => '$' + Math.round(v).toLocaleString('ru-RU') },
  impressions: { label: 'Показы', color: '#8dd6ff', fmt: (v) => Math.round(v).toLocaleString('ru-RU') },
  clicks: { label: 'Клики', color: '#8c93fb', fmt: (v) => Math.round(v).toLocaleString('ru-RU') },
  ctr: { label: 'CTR', color: '#d29922', fmt: (v) => v.toFixed(2) + '%' },
  leads: { label: 'Лиды', color: '#a4aea6', fmt: (v) => Math.round(v).toLocaleString('ru-RU') },
};
const HISTORY_RANGES = [7, 30, 90];

export default function StatsPage() {
  const theme = useTheme();
  const [period, setPeriod] = useState('today');
  const [rangeFrom, setRangeFrom] = useState('');
  const [rangeTo, setRangeTo] = useState('');
  const rangeMode = Boolean(rangeFrom && rangeTo);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  // История / графики
  const [history, setHistory] = useState([]);
  const [metric, setMetric] = useState('spend');
  const [hDays, setHDays] = useState(30);
  const [syncing, setSyncing] = useState(false);

  // Группы (фильтр)
  const [group, setGroup] = useState('');
  const [groups, setGroups] = useState([]);
  useEffect(() => {
    axios.get('/accounts').then((r) => setGroups([...new Set((r.data || []).map((a) => a.group).filter(Boolean))])).catch(() => {});
  }, []);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (rangeMode) {
        // произвольный диапазон — из сохранённой истории (ban-proof)
        const res = await axios.get('/stats/range', { params: { from: rangeFrom, to: rangeTo, group: group || undefined } });
        setRows(res.data.accounts || []);
        setHistory(res.data.series || []);
      } else {
        const res = await axios.get('/stats/all/summary', { params: { period, group: group || undefined } });
        const data = res.data;
        if (Array.isArray(data)) setRows(data);
        else if (data && Array.isArray(data.accounts)) setRows(data.accounts);
        else if (data && Array.isArray(data.results)) setRows(data.results);
        else setRows([]);
      }
      setLastUpdated(new Date());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [period, group, rangeMode, rangeFrom, rangeTo]);

  const fetchHistory = useCallback(async () => {
    if (rangeMode) return;   // в режиме диапазона график берётся из /stats/range
    try {
      const res = await axios.get('/history', { params: { days: hDays, group: group || undefined } });
      setHistory(res.data.series || []);
    } catch (e) {
      setHistory([]);
    }
  }, [hDays, group, rangeMode]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const doSync = async () => {
    setSyncing(true);
    try { await axios.post('/sync', null, { params: { days: 90 } }); } catch (e) { /* ignore */ }
    setSyncing(false);
    fetchHistory();
    fetchStats();
  };

  const totals = rows.reduce(
    (acc, r) => {
      acc.spend += r.spend || 0;
      acc.impressions += r.impressions || 0;
      acc.clicks += r.clicks || 0;
      acc.leads += r.leads || 0;
      return acc;
    },
    { spend: 0, impressions: 0, clicks: 0, leads: 0 }
  );
  totals.ctr = totals.impressions > 0 ? (totals.clicks / totals.impressions) * 100 : 0;
  totals.cpm = totals.impressions > 0 ? (totals.spend / totals.impressions) * 1000 : 0;
  totals.cpc = totals.clicks > 0 ? totals.spend / totals.clicks : 0;
  totals.cpl = totals.leads > 0 ? totals.spend / totals.leads : 0;

  const m = METRICS[metric];
  const chartData = history.map((d) => ({
    date: d.date.slice(5),
    value: metric === 'ctr' ? (d.impressions > 0 ? (d.clicks / d.impressions) * 100 : 0) : (d[metric] || 0),
  }));

  const headCells = ['Аккаунт', 'Расход', 'Показы', 'Клики', 'CTR', 'CPM', 'CPC', 'Лиды', 'CPL', 'Статус'];
  const numCell = { textAlign: 'right', whiteSpace: 'nowrap', color: 'text.primary' };
  // стиль нативного date-инпута под тёмную тему (включая тёмный календарь через color-scheme)
  const dateInputSx = (val) => ({
    colorScheme: 'dark', bgcolor: 'transparent', border: 'none', outline: 'none',
    color: val ? 'text.primary' : 'text.disabled', fontSize: 13, fontFamily: 'inherit', width: 112,
    '&::-webkit-calendar-picker-indicator': { filter: 'invert(0.6)', cursor: 'pointer' },
  });

  return (
    <Page
      title="Статистика"
      subtitle={lastUpdated ? `Обновлено: ${lastUpdated.toLocaleTimeString('ru-RU')}` : 'Данные по рекламным аккаунтам'}
      action={
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={doSync} disabled={syncing} variant="contained" startIcon={<SyncRoundedIcon />}>
            {syncing ? 'Синхронизация…' : 'Синхронизировать'}
          </Button>
          <Button onClick={fetchStats} disabled={loading} variant="outlined" startIcon={<RefreshRoundedIcon />}>
            Обновить
          </Button>
        </Box>
      }
    >
      {/* Период (для живой таблицы и сводки) */}
      <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
        {PERIODS.map((p) => (
          <Button key={p.value} onClick={() => { setRangeFrom(''); setRangeTo(''); setPeriod(p.value); }}
            variant={!rangeMode && period === p.value ? 'contained' : 'text'}
            sx={{ minWidth: 0, px: 2, color: !rangeMode && period === p.value ? undefined : 'text.secondary' }}>
            {p.label}
          </Button>
        ))}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 1.25, ml: 0.5, borderLeft: '1px solid', borderColor: 'divider' }}>
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary', whiteSpace: 'nowrap' }}>Период</Typography>
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 0.5, px: 1, height: 36, borderRadius: '8px',
            border: '1px solid', borderColor: rangeMode ? 'primary.main' : 'divider', bgcolor: 'background.paper',
            transition: 'border-color 0.15s',
          }}>
            <Box component="input" type="date" value={rangeFrom} onChange={(e) => setRangeFrom(e.target.value)} sx={dateInputSx(rangeFrom)} />
            <Box sx={{ color: 'text.disabled', fontSize: 13 }}>–</Box>
            <Box component="input" type="date" value={rangeTo} onChange={(e) => setRangeTo(e.target.value)} sx={dateInputSx(rangeTo)} />
          </Box>
          {rangeMode && (
            <Button size="small" onClick={() => { setRangeFrom(''); setRangeTo(''); }} sx={{ minWidth: 0, px: 1, color: 'text.secondary' }}>Сбросить</Button>
          )}
        </Box>
        {groups.length > 0 && (
          <FormControl size="small" sx={{ minWidth: 160, ml: 'auto' }}>
            <InputLabel>Группа</InputLabel>
            <Select label="Группа" value={group} onChange={(e) => setGroup(e.target.value)}>
              <MenuItem value="">Все группы</MenuItem>
              {groups.map((g) => <MenuItem key={g} value={g}>{g}</MenuItem>)}
            </Select>
          </FormControl>
        )}
      </Box>

      {/* Сводка */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1.75, mb: 3 }}>
        <StatCard icon={<PaidRoundedIcon />} featured value={fmtCurrency(totals.spend)} label="Расход" />
        <StatCard icon={<VisibilityRoundedIcon />} value={fmtInt(totals.impressions)} label="Показы" />
        <StatCard icon={<TouchAppRoundedIcon />} value={fmtInt(totals.clicks)} label="Клики" />
        <StatCard icon={<FlagRoundedIcon />} value={fmtLeads(totals.leads)} label="Лиды" />
      </Box>

      {/* График динамики по дням */}
      <Card sx={{ mb: 3, p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: 'text.primary' }}>Динамика по дням</Typography>
          {rangeMode ? (
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>Период: {rangeFrom} — {rangeTo} · из истории</Typography>
          ) : (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {HISTORY_RANGES.map((d) => (
                <Button key={d} size="small" onClick={() => setHDays(d)} variant={hDays === d ? 'contained' : 'text'}
                  sx={{ minWidth: 0, px: 1.5, color: hDays === d ? undefined : 'text.secondary' }}>
                  {d}д
                </Button>
              ))}
            </Box>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 0.75, mb: 2, flexWrap: 'wrap' }}>
          {Object.keys(METRICS).map((key) => (
            <Chip key={key} label={METRICS[key].label} onClick={() => setMetric(key)} size="small"
              variant={metric === key ? 'filled' : 'outlined'}
              sx={{
                fontWeight: 600, cursor: 'pointer',
                bgcolor: metric === key ? `${METRICS[key].color}22` : 'transparent',
                color: metric === key ? METRICS[key].color : 'text.secondary',
                borderColor: metric === key ? METRICS[key].color : 'divider',
              }} />
          ))}
        </Box>

        {chartData.length === 0 ? (
          <Box sx={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary', fontSize: 13 }}>
            Нет данных за период. Нажми «Синхронизировать», чтобы загрузить историю.
          </Box>
        ) : (
          <Box sx={{ height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="metricGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={m.color} stopOpacity={0.45} />
                    <stop offset="100%" stopColor={m.color} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                  axisLine={false} tickLine={false} minTickGap={24} />
                <YAxis tick={{ fontSize: 11, fill: theme.palette.text.secondary }} axisLine={false} tickLine={false} width={48}
                  tickFormatter={(v) => (metric === 'spend' ? '$' + Math.round(v) : metric === 'ctr' ? v.toFixed(1) + '%' : Math.round(v).toLocaleString('ru-RU'))} />
                <Tooltip
                  contentStyle={{
                    background: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 6, fontSize: 12, color: theme.palette.text.primary,
                  }}
                  labelStyle={{ color: theme.palette.text.secondary }}
                  formatter={(v) => [m.fmt(v), m.label]} />
                <Area type="monotone" dataKey="value" stroke={m.color} strokeWidth={2.5} fill="url(#metricGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </Box>
        )}
      </Card>

      {error && (
        <Box sx={{ mb: 2, p: 1.5, borderRadius: '6px', bgcolor: 'error.main', color: '#fff', fontSize: 13, opacity: 0.9 }}>
          Ошибка: {error}
        </Box>
      )}

      {/* Живая таблица */}
      <Card sx={{ p: 0, overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small" sx={{ '& td, & th': { borderColor: 'divider' } }}>
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                {headCells.map((h, idx) => (
                  <TableCell key={h} sx={{
                    textAlign: idx === 0 ? 'left' : 'right',
                    color: 'text.secondary', fontWeight: 700, fontSize: 11,
                    textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap',
                  }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={10} sx={{ textAlign: 'center', py: 5, color: 'text.secondary' }}>Нет данных</TableCell>
                </TableRow>
              )}
              {rows.map((row, i) => (
                <TableRow key={row.account_id || i} hover>
                  <TableCell sx={{ color: 'text.primary', fontWeight: 600 }}>
                    <div>{row.account_name || row.account_id}</div>
                    {row.account_name && <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{row.account_id}</Typography>}
                  </TableCell>
                  <TableCell sx={numCell}>{fmtCurrency(row.spend)}</TableCell>
                  <TableCell sx={numCell}>{fmtInt(row.impressions)}</TableCell>
                  <TableCell sx={numCell}>{fmtInt(row.clicks)}</TableCell>
                  <TableCell sx={numCell}>{fmtPct(row.ctr)}</TableCell>
                  <TableCell sx={numCell}>{fmtCurrency(row.cpm)}</TableCell>
                  <TableCell sx={numCell}>{fmtCurrency(row.cpc)}</TableCell>
                  <TableCell sx={{ ...numCell, color: 'primary.main', fontWeight: 700 }}>{fmtLeads(row.leads)}</TableCell>
                  <TableCell sx={{ ...numCell, color: 'primary.main', fontWeight: 700 }}>{fmtCurrency(row.cpl)}</TableCell>
                  <TableCell sx={{ textAlign: 'right' }}>
                    <Chip size="small" label={row.status || '—'} color={STATUS_COLOR[row.status] || 'default'} variant="outlined" sx={{ height: 22, fontSize: 11, fontWeight: 600 }} />
                  </TableCell>
                </TableRow>
              ))}
              {rows.length > 0 && (
                <TableRow sx={{ bgcolor: 'action.hover', '& td': { fontWeight: 800, borderTop: '2px solid', borderColor: 'divider' } }}>
                  <TableCell sx={{ color: 'text.primary' }}>ИТОГО</TableCell>
                  <TableCell sx={numCell}>{fmtCurrency(totals.spend)}</TableCell>
                  <TableCell sx={numCell}>{fmtInt(totals.impressions)}</TableCell>
                  <TableCell sx={numCell}>{fmtInt(totals.clicks)}</TableCell>
                  <TableCell sx={numCell}>{fmtPct(totals.ctr)}</TableCell>
                  <TableCell sx={numCell}>{fmtCurrency(totals.cpm)}</TableCell>
                  <TableCell sx={numCell}>{fmtCurrency(totals.cpc)}</TableCell>
                  <TableCell sx={{ ...numCell, color: 'primary.main' }}>{fmtLeads(totals.leads)}</TableCell>
                  <TableCell sx={{ ...numCell, color: 'primary.main' }}>{fmtCurrency(totals.cpl)}</TableCell>
                  <TableCell />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Page>
  );
}
